import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ParseKeys } from 'i18next';
import {
  HttpError,
  type MiceOrderEventView,
  type MiceProfileBulkResolve,
  type MiceProfileBulkResult,
  type MiceProfileResolve,
} from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { miceRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useOrgContext, useSnackbar } from '@web/ui/core';
import { PROFILE_BULK_BATCH, chunks } from '../travelers/eligibility';
import { errorMessage } from './use_mice_orders';

/** Recusas da rota do cadastro com texto próprio na tela (o do servidor nunca aparece). */
const PROFILE_ERROR: Record<string, ParseKeys> = {
  profile_changed: 'mice.profile.error.changed',
  profile_already_resolved: 'mice.profile.error.alreadyResolved',
  profile_busy: 'mice.profile.error.busy',
  profile_traveler_changed: 'mice.profile.error.travelerChanged',
  profile_traveler_unknown: 'mice.profile.error.travelerUnknown',
  profile_missing: 'mice.profile.error.missing',
  profile_value_missing: 'mice.profile.error.nameSplit',
};

const codeOf = (err: HttpError): unknown =>
  (err.details?.details as { code?: unknown } | undefined)?.code;

/** O texto do erro da decisão: a recusa do legado vem com a mensagem dele, já mascarada. */
function profileErrorMessage(err: unknown): string {
  if (!(err instanceof HttpError)) return errorMessage(err);
  if (err.status === 403) return t('mice.profile.error.forbidden');
  const code = codeOf(err);
  if (code === 'legacy_rejected') return err.message;
  const key = typeof code === 'string' ? PROFILE_ERROR[code] : undefined;
  return key ? t(key) : errorMessage(err);
}

/** Decisão do cadastro de UM viajante; o erro fica com a seção que a pediu. */
export function useProfileResolve(projectId: string) {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: queryKeys.mice.project(orgId, projectId) });
  const mutation = useMutation({
    mutationFn: ({ travelerId, body }: { travelerId: string; body: MiceProfileResolve }) =>
      miceRepository.resolveProfile(orgId, projectId, travelerId, body),
    onSuccess: async () => {
      await refresh();
      showSnackbar({ severity: 'success', message: t('mice.profile.done') });
    },
    // 409: a checagem mudou (ou já foi decidida) — a tela relê para o operador conferir de novo.
    onError: async (err) => {
      if (err instanceof HttpError && err.status === 409) await refresh();
    },
    meta: { silent: true },
  });
  return {
    /** `onNameSplit`: o servidor não tem Nome e Sobrenome da planilha (`profile_value_missing`). */
    resolve: (
      travelerId: string,
      body: MiceProfileResolve,
      on: { onDone: () => void; onNameSplit: () => void },
    ): void => {
      mutation.mutate(
        { travelerId, body },
        {
          onSuccess: on.onDone,
          onError: (err) => {
            if (err instanceof HttpError && codeOf(err) === 'profile_value_missing') {
              on.onNameSplit();
            }
          },
        },
      );
    },
    pending: mutation.isPending,
    error: mutation.error ? profileErrorMessage(mutation.error) : null,
  };
}

/** Falhas listadas no aviso do lote; as demais viram "mais N". */
const BULK_FAILURES_SHOWN = 3;

/** Quantos foram e, de quem falhou, o nome e o motivo (o servidor já manda mascarado). */
function bulkMessage(
  result: MiceProfileBulkResult,
  nameOf: (travelerId: string) => string,
): string {
  const done = t('mice.profile.bulk.done', { count: result.updated });
  if (result.failed.length === 0) return done;
  const listed = result.failed
    .slice(0, BULK_FAILURES_SHOWN)
    .map((f) => `${nameOf(f.travelerId)}: ${f.message}`);
  const rest = result.failed.length - listed.length;
  if (rest > 0) listed.push(t('mice.profile.bulk.more', { count: rest }));
  const failed = t('mice.profile.bulk.failed', { count: result.failed.length });
  return `${done} · ${failed} — ${listed.join('; ')}`;
}

/** Um lote falhou depois de outros gravados: a tela diz quantos já foram, além do erro. */
class PartialProfileBulkError extends Error {
  constructor(
    readonly partial: MiceProfileBulkResult,
    override readonly cause: unknown,
  ) {
    super('cadastro em lote parcial');
  }
}

/** "Gravar dados da planilha" / "Manter cadastro da VOLL" na seleção. */
export function useProfileBulk(projectId: string) {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: queryKeys.mice.project(orgId, projectId) });
  /** Lotes em série, somados: uma seleção grande nunca passa do teto do pedido. Um lote que falha
   *  depois de outros gravados para a série e vira `PartialProfileBulkError`. */
  const resolveInBatches = async ({
    travelerIds,
    choice,
  }: MiceProfileBulkResolve): Promise<MiceProfileBulkResult> => {
    const total: MiceProfileBulkResult = { updated: 0, failed: [] };
    for (const batch of chunks(travelerIds, PROFILE_BULK_BATCH)) {
      try {
        const result = await miceRepository.resolveProfiles(orgId, projectId, {
          travelerIds: batch,
          choice,
        });
        total.updated += result.updated;
        total.failed.push(...result.failed);
      } catch (cause: unknown) {
        if (total.updated === 0 && total.failed.length === 0) throw cause;
        throw new PartialProfileBulkError(total, cause);
      }
    }
    return total;
  };
  const mutation = useMutation({
    mutationFn: (
      body: MiceProfileBulkResolve & { onDone?: () => void; nameOf: (id: string) => string },
    ) => resolveInBatches(body),
    onSuccess: async (result, { onDone, nameOf }) => {
      onDone?.();
      await refresh();
      showSnackbar({
        severity: result.failed.length > 0 ? 'warning' : 'success',
        message: bulkMessage(result, nameOf),
      });
    },
    onError: async (err, { onDone, nameOf }) => {
      if (err instanceof PartialProfileBulkError) {
        onDone?.();
        await refresh();
        const partial = bulkMessage(err.partial, nameOf);
        showSnackbar({
          severity: 'error',
          message: `${partial} · ${profileErrorMessage(err.cause)}`,
        });
        return;
      }
      showSnackbar({ severity: 'error', message: profileErrorMessage(err) });
    },
    meta: { silent: true },
  });
  return {
    /** `nameOf`: o nome que o aviso mostra para cada viajante que falhou. */
    resolveBulk: (
      travelerIds: string[],
      choice: MiceProfileBulkResolve['choice'],
      on: { onDone?: () => void; nameOf: (travelerId: string) => string },
    ): void => {
      mutation.mutate({ travelerIds, choice, ...on });
    },
    busy: mutation.isPending,
  };
}

/** As decisões do cadastro de um viajante, do feed do evento filtrado pelo e-mail dele. */
export function useProfileHistory(
  projectId: string,
  travelerEmail: string,
): { events: MiceOrderEventView[]; isError: boolean } {
  const { orgId } = useOrgContext();
  const query = useQuery({
    queryKey: [...queryKeys.mice.activity(orgId, projectId), { travelerEmail }],
    queryFn: () =>
      miceRepository.listActivity(orgId, projectId, null, {
        email: travelerEmail,
        kinds: 'profile',
      }),
  });
  return {
    events: query.data?.items ?? [],
    isError: query.isError,
  };
}
